import React, { useEffect, useState } from "react";
import { RootStateOrAny, useSelector } from "react-redux";
import { Form } from "antd";
import omit from "lodash/omit";
import ToggleableVersionsTable from "../ToggleableVersionsTable/ToggleableVersionsTable";
import { toDisplayFormat, toHttpFormat, toHttpFormatStartOfDay } from "../../lib/utils/formatDate";
import { useAccountInfoContainerContext } from "../../containers/AccountInfoContainer/AccountInfoContainerContext";
import { checkIsLoaded, checkIsLoading } from "../../lib/utils/checkLoadingStatus";
import versionsTableUtils from "../../lib/utils/versionsTableUtils";
import { hasAccess } from "../RolesModule/hasAccess";
import { MENU_ACCOUNT_ACCOUNTINFO_WRITE } from "../../lib/const/permissions";
import ChangeVersionsTable from "../common/ChangeVersionsTable";

interface IProps {
  addressType: "legalAddress" | "actualAddress";
}

const IndividualAddressTable = (props: IProps) => {
  const { addressType } = props;
  const {
    accountAddresses,
    accountAddressesLoading,
    legalAddressesVersions,
    actualAddressesVersions,
    legalAddressesVersionsLoading,
    actualAddressesVersionsLoading,
  } = useSelector((state: RootStateOrAny) => state.accounts);
  const { countries } = useSelector((state: RootStateOrAny) => state.dicts);
  const [tableData, setTableData] = useState<any>([]);
  const [childTableData, setChildTableData] = useState<any>([]);
  // console.log({ tableData });
  
  const [form] = Form.useForm();

  const isChangingForbidden = versionsTableUtils.getIsTableChanging(childTableData);

  const { fetchAddressVersions, changeAddressVersion } = useAccountInfoContainerContext();

  const currentVersionsLoaded = () => {
    if (addressType === "legalAddress") {
      return checkIsLoaded(legalAddressesVersionsLoading);
    }
    return checkIsLoaded(actualAddressesVersionsLoading);
  };

  const currentVersionsLoading = () => {
    if (addressType === "legalAddress") {
      return checkIsLoading(legalAddressesVersionsLoading);
    }
    return checkIsLoading(actualAddressesVersionsLoading);
  };

  const neededVersions = () => {
    if (addressType === "legalAddress") {
      return legalAddressesVersions;
    }
    return actualAddressesVersions;
  };

  useEffect(() => {
    if (checkIsLoaded(accountAddressesLoading)) {
      const accountAddress = accountAddresses[addressType];
      if (accountAddress) {
        const country = versionsTableUtils.geDicValueById(countries, accountAddress?.countryId)
          ?.name;

        const unstructuredAddress = [
          country,
          accountAddress?.district,
          accountAddress?.city,
          accountAddress?.village,
          accountAddress?.street,
          accountAddress?.house,
          accountAddress?.building,
          accountAddress?.apartment,
        ]
          .filter(Boolean)
          .join(", ");

        const data = accountAddress?.unstructuredAddress
          ? { ...accountAddress, key: accountAddress.id }
          : { ...accountAddress, unstructuredAddress, key: accountAddress?.id };

        setTableData([data]);
      }
    }
  }, [accountAddressesLoading]);

  useEffect(() => {
    const versions = neededVersions();
    if (currentVersionsLoaded()) {
      setChildTableData(
        versions[0]
          .map((version, i) => ({
            ...version,
            ...versionsTableUtils.getCommonChildTableProps(version, i, versions[0].length),
          }))
          .sort(versionsTableUtils.sortVersionsByDate)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legalAddressesVersionsLoading, actualAddressesVersionsLoading]);

  const columnsWidths = {
    ...versionsTableUtils.columnsWidths,
    addressType: "20%",
    unstructuredAddress: "",
  };

  const [columns, setColumns] = useState([
    {
      title: "Тип адреса",
      dataIndex: "addressType",
      key: "addressType",
      width: columnsWidths.addressType,
      render: () => {
        if (addressType === "legalAddress") {
          return "Адрес регистрации";
        }
        return "Фактический адрес";
      },
    },
    {
      title: "Адрес",
      dataIndex: "unstructuredAddress",
      key: "unstructuredAddress",
      width: columnsWidths.unstructuredAddress,
    },
    {
      title: "Период действия",
      key: "period",
      dataIndex: "period",
      width: columnsWidths.period,
      render: (text, record) =>
        `${toDisplayFormat(record.fromDate)} - ${toDisplayFormat(record.toDate)}`,
    },
    {
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      hidden: false,
    },
  ]);

  const getChildColumns = () => [
    {
      title: "Тип адреса",
      dataIndex: "addressType",
      key: "addressType",
      width: columnsWidths.addressType,
      render: () => {
        if (addressType === "legalAddress") {
          return "Юридический адрес";
        }
        return "Почтовый адрес";
      },
    },
    {
      title: "Адрес",
      dataIndex: "unstructuredAddress",
      key: "unstructuredAddress",
      width: columnsWidths.unstructuredAddress,
      render: (v, record) => (
        <ChangeVersionsTable.Input
          name="unstructuredAddress"
          record={record}
          setFieldsValue={form.setFieldsValue}
          required
        />
      ),
    },
    {
      title: "Период действия",
      key: "period",
      dataIndex: "period",
      width: columnsWidths.period,
      render: (text, record) => (
        <ChangeVersionsTable.Period
          record={record}
          setFieldsValue={form.setFieldsValue}
          fromDateRequired
          toDateRequired
        />
      ),
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
      render: (value, record) => {
        const isHasAccess = hasAccess(MENU_ACCOUNT_ACCOUNTINFO_WRITE);
        return (
          <>
            <ChangeVersionsTable.AddButton
              hasAccess={isHasAccess}
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.EditButton
              hasAccess={isHasAccess}
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.SubmitButton record={record} submit={form.submit} />
            <ChangeVersionsTable.CancelButton
              record={record}
              resetFields={form.resetFields}
              setChildTableData={setChildTableData}
            />
          </>
        );
      },
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      hidden: false,
    },
  ];

  const [childColumns, setChildColumns] = versionsTableUtils.useChildColumns(getChildColumns, [
    isChangingForbidden,
    tableData,
  ]);

  const onFinish = (values) => {
    const editableRecord = versionsTableUtils.getChangeableRecord(childTableData);
    if (editableRecord.isCreated || editableRecord.isEditing) {
      const isForceUpdate = Boolean(editableRecord.isEditing);
      const fromDate = isForceUpdate
        ? toHttpFormat(editableRecord.fromDate)
        : toHttpFormatStartOfDay(values.fromDate);
      const addressObj = {
        ...omit(editableRecord, [
          "key",
          "isClosing",
          "isCreated",
          "isCurrentVersion",
          "isLatestVersion",
          "isPastVersion",
          "fromDate",
          "toDate",
          "isEditing",
        ]),
        unstructuredAddress: values.unstructuredAddress,
      };

      changeAddressVersion({
        addressId: editableRecord.id,
        fromDate,
        addressObj,
        addressType,
        force: isForceUpdate,
      });
    }
  };

  return (
    <Form form={form} onFinish={onFinish}>
      <ToggleableVersionsTable
        tableName="individual-address-table"
        columns={columns}
        setColumns={setColumns}
        childTableColumns={childColumns}
        setChildTableColumns={setChildColumns}
        tableData={tableData}
        childTableData={childTableData}
        isChildTableLoading={currentVersionsLoading()}
        tableClassName="individual-address-table"
        isPageable={false}
        isToggleableColumns={false}
        onOpenVersionsCallback={(record) => {
          fetchAddressVersions({ addressId: record.id, addressType });
        }}
      />
    </Form>
  );
};

export default IndividualAddressTable;
